/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 18/02/22
 */

/**
 * Make axis ticks for chart.
 *
 * @param data data values
 * @param nominalAxisMax minimum value of axis
 * @return tuple of max and number of axis ticks
 */
export function makeAxis(data: number[], nominalAxisMax = 0): [number, number] {
    const max = Math.max(nominalAxisMax, ...data);
    const rnd = Math.pow(10, Math.floor(Math.log10(max)));

    const display = Math.ceil(max / rnd) * rnd;
    let ticks = Math.floor(display / rnd);
    while (ticks < 4) ticks *= 2;
    ticks = ticks + 1;

    return [display, ticks];
}

let ctx: CanvasRenderingContext2D | null = null;

/**
 * Measures the width of text.
 *
 * @param text text to measure
 * @param fontSize font size in pixels
 * @param font font name - default 'Source Sans Pro'
 * @return width of font in pixels
 */
export function measureTextWidth(text: string, fontSize: number, font: string): number {
    if (!ctx) {
        const canvas = document.createElement('canvas');
        ctx = canvas.getContext('2d');
    }

    if (!ctx) return 0;

    ctx.font = `${fontSize}px ${font}`;
    return ctx.measureText(text).width;
}

/**
 * Truncate text to size if it exceeds specified width when rendered with
 * font and size.
 *
 * @param text text to truncate
 * @param width maximum width the text is allowed to take
 * @param fontSize font size of text
 * @param font font name of text
 */
export function cutToSize(text: string, width: number, fontSize: number, font: string): string {
    const size = measureTextWidth(text, fontSize, font);
    if (size < width) return text;

    const words = text.split(' ');

    while (words.length > 0 && measureTextWidth(words.join(' '), fontSize, font) > width) {
        words.splice(words.length - 1, 1);
    }

    return words.join(' ') + '...';
}

/**
 * Wrap text so that it does not exceed specified width when rendered
 * with font and size.
 *
 * @param text
 * @param width
 * @param fontSize
 * @param font
 * @returns lines of text
 */
export function wrapToLines(text: string, width: number, fontSize: number, font: string): string[] {
    if (measureTextWidth(text, fontSize, font) < width) return [text];

    const words = text.split(' ');
    const lines = [];

    let s = 0;
    for (let i = 1; i < words.length + 1; i++) {
        if (measureTextWidth(words.slice(s, i).join(' '), fontSize, font) > width) {
            if (i - s === 1) lines.push(words[s]);
            else lines.push(words.slice(s, i - 1).join(' '));

            s = i - 1;
        }
    }

    if (s !== words.length) {
        lines.push(words.slice(s).join(' '));
    }

    return lines;
}
